import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs, { Dayjs } from 'dayjs';
import { CostCalculationExternalSurcharge } from 'generated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';

import {
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker,
  Drawer,
  showSnackbar
} from 'components/shared';
import ControlledFunctionSelector from 'components/shared/ControlledFields/ControlledFunctionSelector';
import ControlledMonetaryField from 'components/shared/ControlledFields/ControlledMonetaryField';
import ControlledPercentageField from 'components/shared/ControlledFields/ControlledPercentageField';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import ValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import ControlledWeekdaysPicker from 'components/shared/ControlledFields/ControlledWeekdaysPicker';
import DrawerFooter from 'components/shared/Drawer/DrawerFooter';

import costCalculationExternalsSurchargeSchema from './cost-calculation-external-surcharge-schema';

const schema = costCalculationExternalsSurchargeSchema();
type Form = InferType<typeof schema>;
const minTime = dayjs('1969-12-31T23:00:00Z');
const maxTime = dayjs('1970-01-01T23:00:00Z');

export type AddEditCostCalculationExternalSurchargesProperties = {
  employmentId?: number;
  organizationalUnitId?: number;
  isOpen: boolean;
  surcharge: CostCalculationExternalSurcharge | null;
  handleClose: () => void;
};

export function AddEditCostCalculationExternalSurcharge({
  employmentId,
  organizationalUnitId,
  isOpen,
  surcharge,
  handleClose
}: AddEditCostCalculationExternalSurchargesProperties) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const defaultValues = {
    description: surcharge?.description,
    validFrom: dayjs(surcharge?.validFrom),
    validTo: dayjs(
      surcharge?.validTo || dayjs().startOf('year').add(1, 'year')
    ),
    functionId: surcharge?.functionId,
    weekdays: {
      monday: surcharge?.monday || false,
      tuesday: surcharge?.tuesday || false,
      wednesday: surcharge?.wednesday || false,
      thursday: surcharge?.thursday || false,
      friday: surcharge?.friday || false,
      saturday: surcharge?.saturday || false,
      sunday: surcharge?.sunday || false
    },
    timeFrom: dayjs(surcharge?.timeFrom || minTime),
    timeTo: dayjs(surcharge?.timeTo || maxTime),
    surchargeType: surcharge?.surchargeType || 'AMOUNT',
    amount: surcharge?.amount || 0,
    percentage: surcharge?.percentage ? surcharge?.percentage * 100 : 100
  };

  const [currentSurchargeType, setSurchargeType] = useState<string>(
    surcharge?.surchargeType || 'AMOUNT'
  );

  const { control, handleSubmit, reset, setValue, watch } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const validFrom = watch('validFrom');
  const validTo = watch('validTo');

  const surchargeTypeOptions: Option[] = [
    {
      value: 'PERCENTAGE',
      label: t('percentage')
    },
    {
      value: 'AMOUNT',
      label: t('fixedAmount')
    }
  ];

  useEffect(() => {
    reset(defaultValues);
    setSurchargeType(defaultValues.surchargeType);
  }, [surcharge]);

  const {
    mutate: createCostCalculationExternalSurcharge,
    isLoading: isCreateLoading
  } = mutations.useCreateCostCalculationExternalSurcharges({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurcharges(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSurchargeSuccess')
      });
    }
  });

  const {
    mutate: updateCostCalculationExternalSurcharge,
    isLoading: isUpdateLoading
  } = mutations.useUpdateCostCalculationExternalSurcharges({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurcharges(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSurchargeSuccess')
      });
    }
  });

  const onSubmit = async (data: Form) => {
    if (surcharge) {
      updateCostCalculationExternalSurcharge({
        id: surcharge.id,
        employmentId,
        organizationalUnitId,
        functionId: data.functionId,
        surchargeType: data.surchargeType,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        timeFrom: data.timeFrom.toISOString(),
        timeTo: data.timeTo.toISOString(),
        description: data.description,
        monday: data.weekdays.monday,
        tuesday: data.weekdays.tuesday,
        wednesday: data.weekdays.wednesday,
        thursday: data.weekdays.thursday,
        friday: data.weekdays.friday,
        saturday: data.weekdays.saturday,
        sunday: data.weekdays.sunday,
        amount: data.amount,
        percentage: data.percentage ? data.percentage / 100.0 : undefined
      } as CostCalculationExternalSurcharge);
    } else {
      createCostCalculationExternalSurcharge({
        employmentId,
        organizationalUnitId,
        functionId: data.functionId,
        surchargeType: data.surchargeType,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        timeFrom: data.timeFrom.toISOString(),
        timeTo: data.timeTo.toISOString(),
        description: data.description,
        monday: data.weekdays.monday,
        tuesday: data.weekdays.tuesday,
        wednesday: data.weekdays.wednesday,
        thursday: data.weekdays.thursday,
        friday: data.weekdays.friday,
        saturday: data.weekdays.saturday,
        sunday: data.weekdays.sunday,
        amount: data.amount,
        percentage: data.percentage ? data.percentage / 100.0 : undefined
      } as CostCalculationExternalSurcharge);
    }
  };

  function handleSelectSurchargeType(value: string) {
    setSurchargeType(value);
  }

  function onChangeTimeFrom(date: Dayjs | null) {
    setValue('timeFrom', date || minTime);
  }
  function onChangeTimeTo(date: Dayjs | null) {
    while (date?.isAfter(maxTime)) {
      date = date.subtract(1, 'day');
    }
    if (date?.isSame(minTime)) {
      date = maxTime;
    }
    setValue('timeTo', date || maxTime);
  }

  return (
    <>
      <Drawer
        open={isOpen}
        title={t(
          surcharge
            ? 'editCostCalculationSurcharge'
            : 'addCostCalculationSurcharge'
        )}
        onClose={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mt={2}
            ml={2}
            mr={2}
          >
            <Box>
              <ValidFromValidTo
                control={control}
                validFromRequired
                validToRequired
              />
            </Box>
            <Box>
              <ControlledTextField
                control={control}
                name="description"
                label={t('description')}
                required
                fullWidth
              />
            </Box>
            {organizationalUnitId && (
              <Box>
                <ControlledFunctionSelector
                  control={control}
                  name="functionId"
                  label={t('function')}
                  startDate={validFrom}
                  endDate={validTo}
                  onlyPreferred
                  organizationalUnitId={organizationalUnitId}
                  onlySelectable
                  fullWidth
                />
              </Box>
            )}
            <Box>
              <ControlledWeekdaysPicker control={control} name="weekdays" />
            </Box>
            <Box display="flex" flexDirection="row" gap={2}>
              <Box width={'50%'}>
                <ControlledTimePicker
                  control={control}
                  label={t('timeFrom')}
                  name="timeFrom"
                  onChange={(date) => {
                    onChangeTimeFrom(date);
                  }}
                  fullWidth
                  baseDate={minTime}
                />
              </Box>
              <Box width={'50%'}>
                <ControlledTimePicker
                  control={control}
                  label={t('timeTo')}
                  name="timeTo"
                  onChange={(date) => {
                    onChangeTimeTo(date);
                  }}
                  fullWidth
                  baseDate={minTime}
                />
              </Box>
            </Box>
            <Grid container spacing={2.5}>
              <Grid item xs={6}>
                <ControlledSelect
                  control={control}
                  label={t('surchargeType')}
                  name="surchargeType"
                  options={surchargeTypeOptions}
                  handleSelect={(value) =>
                    handleSelectSurchargeType(value as string)
                  }
                />
              </Grid>
              <Grid item xs={6}>
                {currentSurchargeType === 'AMOUNT' ? (
                  <ControlledMonetaryField
                    control={control}
                    label={t('baseAmount')}
                    name="amount"
                    required
                  />
                ) : (
                  <ControlledPercentageField
                    control={control}
                    label={t('percentage')}
                    name="percentage"
                    required
                  />
                )}
              </Grid>
            </Grid>
          </Box>
          <DrawerFooter>
            <Button onClick={handleClose} color="secondary" variant="contained">
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isCreateLoading || isUpdateLoading}
              type="submit"
              color="primary"
              variant="contained"
            >
              {t('save')}
            </LoadingButton>
          </DrawerFooter>
        </form>
      </Drawer>
    </>
  );
}
